/**
 * SART — Sustained Attention to Response Task
 * 
 * High-precision implementation using performance.now() and requestAnimationFrame.
 * Designed for modern Chrome (sub-millisecond timing in practice for key events).
 *
 * Protocol (classic):
 * - Digits 1-9 presented centrally for 250ms
 * - ISI (inter-stimulus interval) 900ms (total SOA ~1150ms)
 * - Target = "3" (no-go). User must withhold response.
 * -  ~22% targets (typical). ~180 trials gives good reliability.
 *
 * Metrics:
 * - Commission errors: responses on no-go trials (primary marker of lapses)
 * - Omission errors: failures to respond on go trials
 * - Mean RT and SD on correct go trials
 * - Coefficient of variation (CV = SD / mean) — robust proxy for mind-wandering
 */

import type { SARTResult } from './types';

const TRIALS = 180;
const TARGET_DIGIT = 3;
const TARGET_PROB = 0.22; // ~22% no-go

const STIM_DURATION = 250;   // ms visible
const ISI = 900;             // blank between trials

export class SARTSession {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private onComplete: (result: SARTResult) => void;
  private onProgress?: (trial: number, errors: number) => void;

  private trials: Array<{
    digit: number;
    isTarget: boolean;
    shownAt: number;
    responded: boolean;
    responseTime: number | null;
    correct: boolean | null;
  }> = [];

  private currentTrial = 0;
  private lastStimTime = 0;
  private state: 'idle' | 'stim' | 'isi' = 'idle';
  private running = false;
  private startTime = 0;
  private interrupted = false;

  private visibilityHandler = () => {
    if (document.hidden && this.running) {
      this.interrupted = true;
    }
  };

  constructor(
    canvas: HTMLCanvasElement,
    onComplete: (result: SARTResult) => void,
    onProgress?: (trial: number, errors: number) => void
  ) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d', { alpha: true })!;
    this.onComplete = onComplete;
    this.onProgress = onProgress;

    // Keyboard handling
    window.addEventListener('keydown', this.handleKey, { passive: false });
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  destroy() {
    window.removeEventListener('keydown', this.handleKey);
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    this.running = false;
  }

  private handleKey = (e: KeyboardEvent) => {
    if (!this.running || this.state !== 'stim') return;

    if (e.code === 'Space' || e.key === ' ') {
      e.preventDefault();
      const t = this.trials[this.currentTrial - 1];
      if (t && !t.responded) {
        t.responded = true;
        t.responseTime = performance.now() - t.shownAt;
      }
    }
  };

  start() {
    this.trials = [];
    this.currentTrial = 0;
    this.running = true;
    this.interrupted = false;
    this.startTime = performance.now();
    this.scheduleNext();
    this.loop();
  }

  private scheduleNext() {
    if (this.currentTrial >= TRIALS) {
      this.finish();
      return;
    }

    const isTarget = Math.random() < TARGET_PROB;
    const digit = isTarget ? TARGET_DIGIT : this.randomNonTarget();

    const trial = {
      digit,
      isTarget,
      shownAt: 0,
      responded: false,
      responseTime: null as number | null,
      correct: null as boolean | null,
    };
    this.trials.push(trial);
    this.currentTrial = this.trials.length;

    // Show stimulus after short random jitter (reduces anticipation)
    const jitter = 40 + Math.random() * 60;
    setTimeout(() => {
      if (!this.running) return;
      trial.shownAt = performance.now();
      this.state = 'stim';
      this.lastStimTime = trial.shownAt;
      this.drawStim(trial.digit);

      // Hide after STIM_DURATION
      setTimeout(() => {
        if (!this.running) return;
        this.state = 'isi';
        this.clearStim();

        // End of ISI → next
        setTimeout(() => {
          if (!this.running) return;
          this.scheduleNext();
        }, ISI);
      }, STIM_DURATION);
    }, jitter);
  }

  private randomNonTarget(): number {
    let d: number;
    do { d = 1 + Math.floor(Math.random() * 9); } while (d === TARGET_DIGIT);
    return d;
  }

  private drawStim(digit: number) {
    const { width, height } = this.canvas;
    this.ctx.fillStyle = '#0a0f0b';
    this.ctx.fillRect(0, 0, width, height);

    // Large, high-contrast digit — easy to read but not exciting
    this.ctx.fillStyle = '#e8d9b8';
    this.ctx.font = '700 92px ui-monospace, SFMono-Regular, Menlo, monospace';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText(String(digit), width / 2, height / 2 + 6);

    // Very subtle fixation cross for the "boring" sustained attention feel
    this.ctx.strokeStyle = 'rgba(163, 176, 158, 0.25)';
    this.ctx.lineWidth = 1;
    const cx = width / 2, cy = height / 2;
    this.ctx.beginPath();
    this.ctx.moveTo(cx - 18, cy);
    this.ctx.lineTo(cx + 18, cy);
    this.ctx.moveTo(cx, cy - 18);
    this.ctx.lineTo(cx, cy + 18);
    this.ctx.stroke();
  }

  private clearStim() {
    const { width, height } = this.canvas;
    this.ctx.fillStyle = '#0a0f0b';
    this.ctx.fillRect(0, 0, width, height);
  }

  private loop = () => {
    if (!this.running) return;

    // Update live UI elements (if present in DOM)
    const trialEl = document.getElementById('sart-trial');
    const errEl = document.getElementById('sart-errors');
    const live = document.getElementById('sart-live');

    if (live) live.classList.remove('hidden');

    const completed = Math.max(0, this.currentTrial - 1);
    if (trialEl) trialEl.textContent = String(completed);
    if (errEl) {
      const comm = this.trials.filter(t => t.isTarget && t.responded).length;
      errEl.textContent = `${comm} commission`;
    }

    if (this.onProgress) {
      const comm = this.trials.filter(t => t.isTarget && t.responded).length;
      this.onProgress(completed, comm);
    }

    requestAnimationFrame(this.loop);
  };

  private finish() {
    this.running = false;

    const goRTs: number[] = [];
    let commission = 0;
    let omission = 0;

    for (const t of this.trials) {
      if (t.isTarget) {
        if (t.responded) commission++;
        // correct withhold = no response
      } else {
        if (!t.responded) {
          omission++;
        } else if (t.responseTime != null) {
          // filter very fast anticipations (<120ms) and extremely slow outliers
          if (t.responseTime > 120 && t.responseTime < 1800) {
            goRTs.push(t.responseTime);
          }
        }
      }
    }

    const n = goRTs.length;
    const meanRT = n > 0 ? goRTs.reduce((a, b) => a + b, 0) / n : 0;
    const variance = n > 1 ? goRTs.reduce((s, rt) => s + (rt - meanRT) ** 2, 0) / (n - 1) : 0;
    const sdRT = Math.sqrt(variance);
    const cvRT = meanRT > 0 ? sdRT / meanRT : 0;

    const result: SARTResult = {
      trials: this.trials.length,
      commissionErrors: commission,
      omissionErrors: omission,
      meanRT: Math.round(meanRT * 10) / 10,
      sdRT: Math.round(sdRT * 10) / 10,
      cvRT: Math.round(cvRT * 10000) / 10000,
      allRTs: goRTs,
      interrupted: this.interrupted,
      durationMs: Math.round(performance.now() - this.startTime),
    };

    // Final UI cleanup
    const live = document.getElementById('sart-live');
    if (live) live.classList.add('hidden');

    this.onComplete(result);
  }
}

// Convenience helper to compute a simple "lapse index" people understand
export function computeLapseIndex(r: SARTResult): number {
  // Weighted: commissions hurt more than variability
  return Math.round((r.commissionErrors * 2.2 + r.cvRT * 80) * 10) / 10;
}
